import { useEffect, useMemo, useRef, useState } from 'react'
import { listCourses, getCourse } from './ggCourses.js'
import { searchCourses } from '../../lib/golfCourseApi.js' // shared course lookup (reference data only)
import { DEFAULT_STROKE_INDEX } from './ggHandicap.js'
import { calcCourseHandicapRaw, calcPlayingHandicap, ALLOWANCE_OPTIONS } from './ggHandicap.js'
import { getPlayable, getGameDef, defaultOptions } from './ggGameRegistry.js'
import { createGame, saveRoster, watchRosters, saveCourse, watchCourses } from './ggGamesData.js'
import { stripSetupKeys } from './ggGamesSanitize.js'
import { saveGameToFile } from './ggGameFile.js'
import { GGC_ONLY, HUSTLER } from '../../config/siteMode.js'
import { HUSTLER_GAME_ICONS } from '../../components/HustlerGames.jsx'
import { Trash2 } from 'lucide-react'
import { lookupGhinHandicap } from '../../lib/ghinLookup.js'
import { DOT_CATALOG } from './ggPlayDots.js'
import { getMemberId, saveMemberGame, updateMemberGame } from '../../lib/memberGgc.js'

// Normalize a search result / stored course into the setup's course shape.
function normalizeCourse(c) {
  const par = Number(c.par) || 71
  return {
    id: String(c.id),
    name: c.name,
    location: c.location || '',
    // Street address + city/state carried so the app can geocode the course's
    // location and resolve yardages for ANY course (see buildPayload → meta.course).
    address: c.address || '',
    city: c.city || '',
    state: c.state || '',
    par,
    pars: Array.isArray(c.pars) && c.pars.length === 18 ? c.pars.map(Number) : Array(18).fill(Math.round(par / 18)),
    strokeIndex: Array.isArray(c.strokeIndex) && c.strokeIndex.length === 18 ? c.strokeIndex.map(Number) : DEFAULT_STROKE_INDEX,
    tees: (c.tees || c.teeBoxes || []).map((t) => ({ name: t.name, slope: t.slope, rating: t.rating })),
  }
}

// Golf Hustler is a general product — it must NOT pre-select Pine Bluff. It starts on a
// neutral placeholder that forces the golfer to search & pick a real course before saving
// (buildPayload blocks while the course is still this sentinel, so no empty-course game is
// ever saved). The PBCC/GGC build keeps its Pine Bluff default.
const PLACEHOLDER_COURSE_ID = '__pick__'
const HUSTLER_PLACEHOLDER_COURSE = normalizeCourse({ id: PLACEHOLDER_COURSE_ID, name: 'Search to pick a course…', par: 72, tees: [] })

// Shared game-setup form (frame). Entered from a game tile (gameKey). Ad-hoc
// players with per-player allowance, order (Wolf rotation) + full shuffle,
// course/tee, options, and the Scats add-on. Rosters save/reload (editable) so
// the same group is one tap next time. Creates the game under ggGames/*.
const GREEN = '#1B4D2E'
const cell = { border: '1px solid #ddd', padding: '6px 10px', fontSize: 16 }
// scatsIn defaults TRUE — like the app, every player is IN Scats by default and
// you uncheck to opt a player OUT (not check-to-add-in).
const blank = () => ({ name: '', hcp: '', allowance: '100', scatsIn: true, skinsIn: true, teeIdx: 0, ghin: '', ghinStatus: 'idle' })

// Side-bet row label icon (Golf Hustler): reuse the SAME tile icon file (scats/
// skins/dots) from the PICK A GAME set; other builds keep the emoji.
const SideIcon = ({ k, emoji, size = 24 }) => (HUSTLER && HUSTLER_GAME_ICONS[k]
  ? <img src={HUSTLER_GAME_ICONS[k]} alt="" aria-hidden="true" style={{ width: size, height: size, objectFit: 'contain', verticalAlign: 'middle', marginRight: 2, display: 'inline-block' }} />
  : <>{emoji}</>)

// Inverse of buildPayload: a saved /ggGames record → the setup form's state, so a
// member can reopen a game and edit it. The stored course is trimmed (one tee),
// so we rebuild a single-tee course; changing the course re-searches as normal.
function gameToSetupState(rec) {
  const meta = rec?.meta || {}
  const players = Array.isArray(rec?.players) ? rec.players : []
  const c = meta.course || {}
  const course = normalizeCourse({
    id: c.id || 'custom', name: c.name || 'Course', par: c.par, pars: c.pars, strokeIndex: c.strokeIndex,
    address: c.address, city: c.city, state: c.state,
    tees: c.teeName ? [{ name: c.teeName, slope: c.slope, rating: c.rating }] : [],
  })
  const a = meta.addons || {}
  const rows = players.map((p) => ({
    name: p.name || '',
    hcp: p.handicapIndex != null ? String(p.handicapIndex) : '',
    allowance: p.allowance != null ? String(p.allowance) : '100',
    scatsIn: !!(a.scats?.optedIn || []).includes(p.id),
    skinsIn: a.skins?.in ? a.skins.in.includes(p.id) : true,
    teeIdx: 0, ghin: p.ghin || '', ghinStatus: 'idle',
  }))
  const opts = { ...(meta.options || {}) }
  if (a.scats) { opts.__scats = true; opts.__scatsBuyIn = a.scats.buyIn; opts.__scatsHcp = !!a.scats.useHandicap; opts.__scatsMode = a.scats.mode; opts.__scatsBet = a.scats.bet || 'Pot'; opts.__scatsScoreMode = a.scats.scoreMode || 'Team' }
  if (a.skins) { opts.__skins = true; opts.__skinsAmount = a.skins.amount; opts.__skinsCarry = a.skins.carry !== false; opts.__skinsDouble = !!a.skins.double; opts.__skinsPayout = a.skins.payout }
  if (a.dots) { opts.__dots = true; opts.__dotValue = a.dots.dotValue; DOT_CATALOG.forEach((d) => { opts['__dot_' + d.key] = (a.dots.dotsInPlay || []).includes(d.key) }) }
  // Team Cup CTH restores its check-to-expand state from the saved pot.
  if ((Number(opts.cthAmount) || 0) > 0) opts.__cth = true
  return { name: meta.name || '', course, teeIdx: 0, handicapsOn: !!meta.handicapsOn, noBets: !!meta.noBets, rows: rows.length ? rows : undefined, opts }
}

// Standard ⓘ info-dot pattern (GGC members' surface): a small circled-i; tap for
// a short popover explanation, no layout space wasted. Tap outside to close.
function InfoDot({ children }) {
  const [open, setOpen] = useState(false)
  return (
    <span className="relative inline-flex items-center">
      <button type="button" title="What's this?" onClick={(e) => { e.preventDefault(); setOpen((v) => !v) }}
        style={HUSTLER
          ? { width: 26, height: 26, borderRadius: 13, border: '1.5px solid #9CC400', color: '#9CC400', background: 'transparent', fontSize: 16, fontStyle: 'italic', fontWeight: 800, lineHeight: '23px', textAlign: 'center', cursor: 'pointer', padding: 0 }
          : { width: 28, height: 28, borderRadius: 14, border: 'none', color: '#fff', background: '#1B4D2E', fontSize: 17, fontStyle: 'italic', fontWeight: 800, lineHeight: '28px', textAlign: 'center', cursor: 'pointer', padding: 0, boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>i</button>
      {open && (
        <>
          <span className="fixed inset-0" style={{ zIndex: 40 }} onClick={(e) => { e.preventDefault(); setOpen(false) }} />
          <span className="absolute rounded-xl shadow-2xl" style={{ zIndex: 50, top: 38, left: 0, width: 460, maxWidth: '94vw', background: '#fff', border: '1.5px solid #C7DFCB', padding: 26, color: '#0F2914', fontWeight: 400, fontSize: 20, lineHeight: 1.6, whiteSpace: 'normal', textTransform: 'none' }}>{children}</span>
        </>
      )}
    </span>
  )
}
// Wolf variant explanations — EXACT wording read from GolfGamesScorecard
// (app/game-setup.tsx wolf variations). Do not paraphrase.
function VariantInfo() {
  return (
    <InfoDot>
      <span style={{ display: 'block', fontWeight: 800, fontSize: 25, marginBottom: 16 }}>Wolf variants</span>
      <span style={{ display: 'block', marginBottom: 16 }}><b>Wolf (standard)</b> — Classic Wolf rotation — Wolf picks a partner or goes lone.</span>
      <span style={{ display: 'block', marginBottom: 16 }}><b>Biggest Loser</b> — Wolf with the last-hole rule: player down the most tees off first.</span>
      <span style={{ display: 'block' }}><b>Biggest Loser Shuck (6+ players)</b> — Last player each hole is the Shuck — Wolf decides take or shuck independently of the earlier pick. Wolf has 4 outcomes per hole.</span>
    </InfoDot>
  )
}

// Members' review page — the website NEVER plays a round; it shows the whole
// game (players + handicaps/tees + variant/bets + Scats/Skins/Dots), then "Send
// and Save" writes the shared /ggGames contract so the companion app can play it
// on the course. Reached from the setup "Review" button (GGC_ONLY).
function GGameReview({ meta, players, busy, sent, onSend, onBack, onDone, edit = false }) {
  // Save-to-file: downloads THIS game (the same meta+players written to /ggGames)
  // as a JSON file the user can keep and re-Import later. Independent of Send.
  const [saveMsg, setSaveMsg] = useState('')
  const [saving, setSaving] = useState(false)
  const onSaveFile = async () => {
    if (saving) return
    setSaveMsg(''); setSaving(true)
    try {
      const r = await saveGameToFile(meta, players)
      setSaveMsg(r.ok ? `Saved to ${r.filename}` : '')
    } catch { setSaveMsg('Could not save the file.') }
    setSaving(false)
  }
  const o = meta.options || {}
  const a = meta.addons || {}
  const net = !!meta.handicapsOn
  const up = (v) => String(v ?? '').toUpperCase()
  const cellR = { border: '1px solid #eee', padding: '9px 12px', textAlign: 'left' }
  const box = { border: '1px solid #E5E7EB', borderRadius: 12, padding: 18, marginBottom: 16 }
  const hd = { fontSize: 13, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280', marginBottom: 10 }
  // Neutral pill for labels; MoneyPill (green) is reserved for dollar amounts only.
  const Pill = ({ children }) => <span className="rounded-full px-4 py-1.5 text-base font-bold" style={{ background: '#F3F4F6', border: '1px solid #E5E7EB', color: '#374151' }}>{children}</span>
  const MoneyPill = ({ children }) => <span className="rounded-full px-4 py-1.5 text-base font-bold" style={{ background: '#EDF4EE', border: '1px solid #C7DFCB', color: GREEN }}>{children}</span>

  // Render the GAME & BETS pills from THIS game's own registry options (not
  // Wolf's). A numeric option whose label carries "$" is the stake → green
  // MoneyPill with the amount slotted into the label ("$ / point" → "$2 / point");
  // everything else is a neutral label pill. Options the game doesn't have never
  // appear, so no blank Variant/Payout pills on non-Wolf games.
  const game = getGameDef(meta.gameId) || {}
  const isMoneyOpt = (opt) => opt.type === 'number' && String(opt.label).includes('$')
  const gamePills = []
  ;(game.options || []).forEach((opt) => {
    const val = o[opt.key]
    if (opt.type === 'bool') { if (val) gamePills.push({ money: false, text: String(opt.label).replace(/^Allow\s+/, '') }); return }
    if (isMoneyOpt(opt)) { if (!meta.noBets && Number(val) > 0) gamePills.push({ money: true, text: String(opt.label).replace('$', '$' + (Number(val) || 0)) }); return }
    if (opt.type === 'number') { gamePills.push({ money: false, text: `${opt.label}: ${val ?? '—'}` }); return }
    gamePills.push({ money: false, text: `${opt.label}: ${up(val)}` }) // select
  })
  if (meta.noBets) gamePills.unshift({ money: false, text: 'No bets' })
  // "Wolf rotation" only for Wolf; other ordered games get a neutral order hint.
  const playersOrder = game.ordered ? (meta.gameId === 'wolf' ? ' · order = Wolf rotation' : ' · player order set') : ''

  if (sent) {
    return (
      <div className="card text-center" style={{ maxWidth: 520, margin: '0 auto' }}>
        <div style={{ fontSize: 52 }}>✅</div>
        <h3 className="text-xl font-extrabold" style={{ color: GREEN }}>{edit ? 'Changes saved' : 'Sent to the app'}</h3>
        <p className="text-gray-600 mt-2" style={{ fontSize: 15 }}><b>{meta.name}</b> {edit ? 'was updated and synced to your member account — the same game, not a copy. Open ' : 'is saved to your member account. Open '}<b>Golf Games</b> on your phone to play it on the course.</p>
        <button className="btn-primary mt-4" style={{ backgroundColor: GREEN }} onClick={onDone}>Done</button>
      </div>
    )
  }
  return (
    <div className="card" style={{ width: '100%', maxWidth: 960, margin: '0 auto' }}>
      <div className="flex items-center gap-3 mb-2">{HUSTLER && HUSTLER_GAME_ICONS[meta.gameId]
        ? <img src={HUSTLER_GAME_ICONS[meta.gameId]} alt="" aria-hidden="true" style={{ width: 40, height: 40, objectFit: 'contain' }} />
        : <span style={{ fontSize: 30 }}>{game.icon || '⛳'}</span>}<h3 className="font-bold text-3xl">Review your game</h3></div>
      <div className="text-base text-gray-700 mb-5">{meta.name} · {meta.course?.name} · Tee: {meta.course?.teeName || '—'} · {net ? `Net ${meta.allowance || 100}%` : 'Gross'}</div>

      {gamePills.length ? (
        <div style={box}>
          <div style={hd}>Game &amp; bets</div>
          <div className="flex flex-wrap gap-2.5">
            {gamePills.map((p, i) => p.money ? <MoneyPill key={i}>{p.text}</MoneyPill> : <Pill key={i}>{p.text}</Pill>)}
          </div>
        </div>
      ) : null}

      {(a.scats || a.skins || a.dots) ? (
        <div style={box}>
          <div style={hd}>Side games</div>
          <div className="flex flex-col gap-2 text-lg font-bold" style={{ color: '#0F2914' }}>
            {a.scats ? (
              <div className="flex flex-wrap items-center gap-2">
                <MoneyPill><SideIcon k="scats" emoji="💰" size={18} /> Scats</MoneyPill>
                <MoneyPill>${a.scats.buyIn} buy-in</MoneyPill>
                {a.scats.useHandicap ? <MoneyPill>net · {up(a.scats.mode)}</MoneyPill> : null}
                <MoneyPill>{a.scats.optedIn?.length || 0} in</MoneyPill>
              </div>
            ) : null}
            {a.skins ? <div><SideIcon k="skins" emoji="💰" size={20} /> <b>Skins</b> — ${a.skins.amount}/skin · {up(a.skins.payout)}{a.skins.carry ? (a.skins.double ? ' · double carry' : ' · carry') : ' · no carry'} · {a.skins.in?.length || 0} in</div> : null}
            {a.dots ? <div><SideIcon k="dots" emoji="💠" size={20} /> <b>Dots</b> — ${a.dots.dotValue}/dot · {(a.dots.dotsInPlay || []).join(', ')}</div> : null}
          </div>
        </div>
      ) : null}

      <div style={box}>
        <div style={hd}>Players ({players.length}){playersOrder}</div>
        <table className="w-full text-base" style={{ borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#f6f6f2', color: '#555' }}>
            <th style={cellR}>#</th><th style={cellR}>Name</th><th style={cellR}>HC Index</th>
            {net ? <th style={cellR}>CH</th> : null}{net ? <th style={cellR}>PH</th> : null}
            <th style={cellR}>Tee</th><th style={cellR}>Allow %</th>
          </tr></thead>
          <tbody>{players.map((p, i) => (
            <tr key={p.id}>
              <td style={cellR}>{i + 1}</td><td style={cellR}>{p.name}</td><td style={cellR}>{p.handicapIndex ?? '—'}</td>
              {net ? <td style={cellR}>{p.courseHandicap}</td> : null}{net ? <td style={cellR}>{p.playingHandicap}</td> : null}
              <td style={cellR}>{p.teeName || '—'}</td><td style={cellR}>{p.allowance}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>

      <div className="flex items-center flex-wrap gap-3 pt-2">
        <button className="btn-primary" disabled={busy} style={{ backgroundColor: GREEN, fontSize: 17, padding: '12px 22px' }} onClick={onSend}>{busy ? 'Saving…' : (edit ? 'Save changes' : 'Send and Save')}</button>
        <button className="btn-secondary" disabled={saving} style={{ fontSize: 17, padding: '12px 22px' }} onClick={onSaveFile} title="Download this game as a file you can keep and re-import later">{saving ? 'Saving…' : 'Save to file'}</button>
        <button className="btn-ghost text-base" onClick={onBack}>Back</button>
        {saveMsg ? <span className="text-sm font-semibold" style={{ color: GREEN }}>{saveMsg}</span> : null}
      </div>
    </div>
  )
}

export default function GGameSetup({ gameKey, onStart, onCancel, initialGame = null, editGameId = null }) {
  const game = getGameDef(gameKey)
  // Games that pick teams the Team Cup way (named teams + click-assign, no A/B
  // auto-split, no Shuffle). Team Cup and Scramble share this.
  const namedTeams = gameKey === 'team-cup' || gameKey === 'scramble'
  // Editing a saved game → prefill the whole form from the record (computed once).
  const preset = useMemo(() => (initialGame ? gameToSetupState(initialGame) : null), [])
  const [name, setName] = useState(preset?.name ?? '')
  const [course, setCourse] = useState(preset?.course ?? (HUSTLER ? HUSTLER_PLACEHOLDER_COURSE : normalizeCourse(getCourse('pinebluff'))))
  const [teeIdx, setTeeIdx] = useState(preset?.teeIdx ?? 0)
  const [handicapsOn, setHandicapsOn] = useState(preset?.handicapsOn ?? false)
  const [noBets, setNoBets] = useState(!!preset?.noBets) // bets ON by default; ON = no money
  const [rows, setRows] = useState(preset?.rows ?? Array.from({ length: Math.max(game.minPlayers, 4) }, blank))
  const [opts, setOpts] = useState(preset?.opts ?? defaultOptions(gameKey))
  // Team Cup: flexible NAMED teams (like the app). playerIdxs reference ROW
  // indices; each player belongs to exactly one team. Not used by other games.
  const [teams, setTeams] = useState([{ id: 't_a', name: '', playerIdxs: [] }, { id: 't_b', name: '', playerIdxs: [] }])
  const [busy, setBusy] = useState(false)
  const [rosters, setRosters] = useState([])
  const [savedCourses, setSavedCourses] = useState([])
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [searchErr, setSearchErr] = useState('')
  useEffect(() => watchRosters(setRosters), [])
  useEffect(() => watchCourses(setSavedCourses), [])

  // Merged pick list: saved/recent courses (from the shared lookup) + built-in samples, deduped by id.
  const pickList = useMemo(() => {
    const seen = new Set()
    const out = []
    // Hustler: while no real course is picked yet, surface the neutral placeholder in the
    // dropdown so the <select> shows "Search to pick…" (not a real course); it drops out of
    // the list the moment a real course is chosen.
    if (HUSTLER && course?.id === PLACEHOLDER_COURSE_ID) { out.push(HUSTLER_PLACEHOLDER_COURSE); seen.add(PLACEHOLDER_COURSE_ID) }
    for (const c of [...savedCourses, ...listCourses()]) {
      const nc = normalizeCourse(c)
      if (seen.has(nc.id)) continue
      seen.add(nc.id); out.push(nc)
    }
    return out
  }, [savedCourses, course?.id])

  // Debounced live course search via the shared golfCourseApi.
  const timer = useRef(null)
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current)
    const q = query.trim()
    if (q.length < 3) { setResults([]); setSearching(false); setSearchErr(''); return }
    setSearching(true); setSearchErr('')
    timer.current = setTimeout(async () => {
      try {
        const hits = await searchCourses(q)
        setResults(hits)
        setSearchErr(hits.length ? '' : 'No matches — try fewer words.')
      } catch {
        // Surface a real failure (dead key / outage / blocked call) — never a silent blank.
        setResults([]); setSearchErr('Search unavailable — check your connection and retry.')
      } finally {
        setSearching(false)
      }
    }, 350)
    return () => timer.current && clearTimeout(timer.current)
  }, [query])

  // Picking a course resets the course-level tee AND every player's tee to the default.
  const pickSaved = (id) => { const c = pickList.find((x) => x.id === id); if (c) { setCourse(c); setTeeIdx(0); setRows((prev) => prev.map((r) => ({ ...r, teeIdx: 0 }))) } }
  const pickResult = (r) => {
    const c = normalizeCourse(r)
    setCourse(c); setTeeIdx(0); setRows((prev) => prev.map((row) => ({ ...row, teeIdx: 0 }))); saveCourse(c); setQuery(''); setResults([])
  }

  // Changing the course-level (default) tee re-baselines every player's tee — same
  // as the app. Skip the very first run so it doesn't clobber the initial rows.
  const teeBaseline = useRef(false)
  useEffect(() => {
    if (!teeBaseline.current) { teeBaseline.current = true; return }
    setRows((prev) => prev.map((r) => ({ ...r, teeIdx })))
  }, [teeIdx])

  const tee = course?.tees?.[teeIdx] || course?.tees?.[0]
  const teeFor = (r) => course?.tees?.[r.teeIdx] || tee
  const named = useMemo(() => rows.filter((r) => r.name.trim()), [rows])
  // App-shaped mods bundle for the saved record so the app forms teams + picks
  // the two-axis money model on import (setup-only games). Teams by player order.
  const buildMods = () => {
    const m = {}
    if (opts.betType) m.betType = opts.betType
    if (game.teams === true) {
      const size = (gameKey === 'best2of4' || gameKey === 'yellowball') ? 4 : 2
      const t = []; for (let i = 0; i < named.length; i += size) t.push(named.map((_, j) => j).slice(i, i + size))
      m.teams = t; m.teamNames = t.map((_, i) => `Team ${i + 1}`)
    }
    if (opts.lhLowTeam) m.lhLowTeam = true
    if (opts.requireAcceptance) m.requireAcceptance = true
    return m
  }
  const isBls = opts.variant === 'blShuck'
  const minPlayers = isBls ? Math.max(game.minPlayers, 6) : game.minPlayers

  const addRow = () => { if (rows.length < game.maxPlayers) setRows([...rows, blank()]) }
  const removeRow = (i) => {
    if (rows.length <= minPlayers) return
    setRows(rows.filter((_, x) => x !== i))
    if (namedTeams) setTeams((prev) => prev.map((t) => ({ ...t, playerIdxs: t.playerIdxs.filter((x) => x !== i).map((x) => (x > i ? x - 1 : x)) })))
  }
  // Total-players box (GGC_ONLY): the number IS the row count. Growing appends
  // blank rows; shrinking removes EMPTY rows from the bottom first and never
  // drops a row that already has a name (so a typed player can't be lost). If
  // only named rows remain, it won't go lower. Range 2–8; "+ Add player" (and
  // game.maxPlayers) can still push beyond 8.
  const setTotalPlayers = (n) => {
    const target = Math.max(2, Math.min(8, Math.floor(Number(n) || 0)))
    setRows((prev) => {
      if (target >= prev.length) return [...prev, ...Array.from({ length: target - prev.length }, blank)]
      const next = [...prev]
      while (next.length > target) {
        let idx = -1
        for (let x = next.length - 1; x >= 0; x--) { if (!next[x].name.trim()) { idx = x; break } }
        if (idx === -1) break // only named rows remain — don't drop below them
        next.splice(idx, 1)
      }
      return next
    })
  }
  const setRow = (i, patch) => setRows(rows.map((r, x) => (x === i ? { ...r, ...patch } : r)))
  const patchRow = (i, patch) => setRows((prev) => prev.map((r, x) => (x === i ? { ...r, ...patch } : r)))
  // GHIN / GH# lookup — reuses the EXISTING web util (lib/ghinLookup.js), the
  // same call TournamentDetail / ClubData / Register use. Fills the Index box
  // from the official handicap index instead of manual typing. GGC_ONLY only.
  const lookupRowGhin = async (i) => {
    const ghin = (rows[i]?.ghin || '').replace(/\D/g, '')
    if (!ghin) return
    patchRow(i, { ghinStatus: 'loading' })
    const res = await lookupGhinHandicap(ghin)
    if (res?.ok) patchRow(i, { hcp: String(res.handicapIndex), ghinStatus: 'ok', name: rows[i]?.name?.trim() ? rows[i].name : (res.fullName || '') })
    else patchRow(i, { ghinStatus: 'miss' })
  }
  const move = (i, dir) => {
    const j = i + dir
    if (j < 0 || j >= rows.length) return
    const next = [...rows]
    ;[next[i], next[j]] = [next[j], next[i]]
    setRows(next)
  }
  const shuffle = () => {
    const a = [...rows]
    for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1));[a[i], a[j]] = [a[j], a[i]] }
    setRows(a)
  }
  const loadRoster = (id) => {
    const r = rosters.find((x) => x.id === id)
    if (r?.rows?.length) setRows(r.rows.map((x) => ({ ...blank(), ...x })))
  }
  const doSaveRoster = () => {
    if (named.length === 0) return
    saveRoster(name.trim() || `${game.label} group`, named.map((r) => ({ name: r.name.trim(), hcp: r.hcp, allowance: r.allowance, scatsIn: r.scatsIn })))
  }

  // ── Team Cup: named-team helpers (click-to-assign, one team per player) ──
  const addTeam = () => setTeams((prev) => [...prev, { id: `t_${prev.length + 1}_${Math.random().toString(36).slice(2, 6)}`, name: '', playerIdxs: [] }])
  const removeTeam = (id) => setTeams((prev) => (prev.length <= 2 ? prev : prev.filter((t) => t.id !== id)))
  const updateTeamName = (id, nm) => setTeams((prev) => prev.map((t) => (t.id === id ? { ...t, name: nm } : t)))
  const togglePlayerOnTeam = (teamId, rowIdx) => setTeams((prev) => prev.map((t) => {
    if (t.id === teamId) { const has = t.playerIdxs.includes(rowIdx); return { ...t, playerIdxs: has ? t.playerIdxs.filter((i) => i !== rowIdx) : [...t.playerIdxs, rowIdx] } }
    return { ...t, playerIdxs: t.playerIdxs.filter((i) => i !== rowIdx) }
  }))
  // Drop assignments for rows that were emptied/removed (keep indices valid).
  useEffect(() => {
    if (!namedTeams) return
    setTeams((prev) => prev.map((t) => ({ ...t, playerIdxs: t.playerIdxs.filter((i) => i < rows.length && rows[i]?.name.trim()) })))
  }, [rows.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const [review, setReview] = useState(null) // GGC: {meta, players} snapshot for the review page
  const [sent, setSent] = useState(false)     // GGC: true once Send and Save wrote the record

  // Build the meta+players payload from the form (validation only, no write).
  const buildPayload = () => {
    // Hustler starts with no course — require a real one before anything saves, so we never
    // reintroduce the empty-course bug. (PBCC/GGC defaults to Pine Bluff, so this never trips.)
    if (HUSTLER && (!course || course.id === PLACEHOLDER_COURSE_ID)) { alert('Pick a course first — search for the course you’re playing, then choose it.'); return null }
    if (named.length < minPlayers) { alert(`Need at least ${minPlayers} named players${isBls ? ' for Biggest Loser Shuck' : ''}.`); return null }
    // Team Cup: resolve each named player's team from the click-assignment
    // (named teams, one team per player). Validates like the app.
    let teamOfNamedIdx = null
    if (namedTeams) {
      const namedRowIdxs = rows.map((r, ri) => ({ ri, ok: !!r.name.trim() })).filter((x) => x.ok).map((x) => x.ri)
      const teamOfRow = {}
      teams.forEach((t, ti) => t.playerIdxs.forEach((ri) => { if (rows[ri]?.name.trim()) teamOfRow[ri] = ti }))
      if (teams.length < 2) { alert('Team Cup needs at least two teams.'); return null }
      if (namedRowIdxs.some((ri) => teamOfRow[ri] == null)) { alert('Every player must be assigned to a team (click a player under a team).'); return null }
      if (teams.some((t) => t.playerIdxs.filter((ri) => rows[ri]?.name.trim()).length === 0)) { alert('Every team needs at least one player.'); return null }
      teamOfNamedIdx = namedRowIdxs.map((ri) => teamOfRow[ri])
    }
    const teamNameFor = (ti) => teams[ti]?.name.trim() || `Team ${ti + 1}`
    const scatsEnabled = !!opts.__scats
    const players = named.map((r, i) => {
      const idx = parseFloat(r.hcp)
      const rowTee = teeFor(r) // each player's OWN tee → its slope/rating
      const chRaw = Number.isFinite(idx) ? calcCourseHandicapRaw({ handicapIndex: idx, slopeRating: rowTee?.slope, courseRating: rowTee?.rating, par: course?.par }) : 0
      const teamIdx = namedTeams ? (teamOfNamedIdx[i] || 0) : (game.teams ? Math.floor(i / Math.ceil(named.length / game.teams)) : 0)
      return {
        id: `p${i + 1}`,
        name: r.name.trim(),
        handicapIndex: Number.isFinite(idx) ? idx : null,
        courseHandicap: Math.round(chRaw),
        allowance: Number(r.allowance) || 100,
        teeName: rowTee?.name || null,
        teeIdx: r.teeIdx ?? 0,
        ghin: r.ghin || null,
        playingHandicap: handicapsOn ? calcPlayingHandicap(chRaw, Number(r.allowance) || 100) : 0,
        // Team games: order-split (A/B) for the rest; Team Cup uses the
        // click-assigned team INDEX + name.
        team: teamIdx,
        ...(namedTeams ? { teamName: teamNameFor(teamIdx) } : {}),
      }
    })
    const optedIn = named.map((r, i) => (r.scatsIn ? `p${i + 1}` : null)).filter(Boolean)
    // "No bets": keep games/side-games ENABLED so they still track + show results,
    // but zero all stake amounts so settlement produces no money.
    const skinsEnabled = !!opts.__skins && (noBets || (Number(opts.__skinsAmount) || 0) > 0)
    const skinsInIds = named.map((r, i) => (r.skinsIn !== false ? `p${i + 1}` : null)).filter(Boolean)
    const addons = {}
    if (scatsEnabled) addons.scats = { enabled: true, buyIn: noBets ? 0 : (Number(opts.__scatsBuyIn) || 10), useHandicap: !!opts.__scatsHcp, mode: opts.__scatsMode || 'cap1', bet: opts.__scatsBet || 'Pot', scoreMode: game.teams === 2 ? (opts.__scatsScoreMode || 'Team') : 'Player', optedIn }
    if (skinsEnabled) addons.skins = { enabled: true, amount: noBets ? 0 : (Number(opts.__skinsAmount) || 0), carry: opts.__skinsCarry !== false, double: !!opts.__skinsDouble, payout: opts.__skinsPayout === 'per-player' ? 'per-player' : 'split-pot', in: skinsInIds }
    const dotsEnabled = !!opts.__dots
    const dotsInPlay = DOT_CATALOG.filter((c) => (opts['__dot_' + c.key] !== undefined ? opts['__dot_' + c.key] : c.default)).map((c) => c.key)
    if (dotsEnabled && dotsInPlay.length) addons.dots = { enabled: true, dotValue: noBets ? 0 : (Number(opts.__dotValue) || 1), dotsInPlay }
    const gameOpts = stripSetupKeys(opts) // drop all __ setup-only keys (Scats/Skins/Dots config etc.)
    gameOpts.mods = buildMods(); gameOpts.betPerHole = Number(opts.stake) || 0
    if (noBets) { gameOpts.baseBet = 0; gameOpts.noBets = true }
    // Team Cup CTH is now a check-to-expand side bet: apply the pot only when
    // the checkbox is on (off → 0). Same cthAmount value the settle reads.
    if (gameKey === 'team-cup') gameOpts.cthAmount = opts.__cth ? (Number(opts.cthAmount) || 0) : 0
    // ── Companion-app carry ──────────────────────────────────────────────────
    // The app ingests game options via meta.options.betPerHole + meta.options.mods
    // (companionMap ggRecordToGameSetupParams → app game-setup → scorecard). We
    // write those ALONGSIDE the flat keys the web settle() reads — additive, so
    // the web side is unchanged. Select values ('Off'/'1'…, 'Pot'/'Hole-by-hole'/
    // 'Match', '1 Down'…) match exactly what the app scorecard parses.
    const mods = {}
    const toMod = (k) => { if (gameOpts[k] !== undefined) mods[k] = gameOpts[k] }
    if (gameKey === 'scotch') {
      const pv = Number(gameOpts.pointValue) || 0
      gameOpts.betPerHole = pv
      gameOpts.baseBet = pv // mirror so the web scotch settle still renders money
      ;['scLowTeam', 'scLowBall', 'scHighBall', 'scBirdie', 'scCtp', 'scBlitz'].forEach(toMod)
    } else if (gameKey === 'nassau') {
      gameOpts.betPerHole = Number(gameOpts.baseBet) || 0
      toMod('pressAt')
    } else if (gameKey === 'vegas') {
      gameOpts.betPerHole = Number(gameOpts.pointValue) || 0
      toMod('tenCap')
    } else if (gameKey === 'stroke-play' || gameKey === 'scramble' || gameKey === 'shamble') {
      gameOpts.betPerHole = Number(gameOpts.betPerHole) || 0
      toMod('betType')
      if (gameKey === 'scramble' || gameKey === 'shamble') {
        const gc = players.reduce((m, p) => Math.max(m, p.team || 0), 0) + 1
        mods.teams = Array.from({ length: gc }, (_, ti) => players.map((_, i) => i).filter((i) => (players[i].team || 0) === ti))
      }
    } else if (gameKey === 'rabbit') {
      // App Rabbit uses ONE combined pot value ($ pot / nine → betPerHole).
      const pot = Number(gameOpts.betPerHole) || 0
      gameOpts.frontPot = pot // mirror both nines so the web computeRabbit settle still renders
      gameOpts.backPot = pot
    } else if (gameKey === 'sixes') {
      // App Sixes stake is per 6-hole segment; carry both keys.
      gameOpts.betPerHole = Number(gameOpts.dollarsPerSegment) || 0
    }
    // Fallback: any other money game → mirror its main stake into betPerHole.
    if (gameOpts.betPerHole === undefined) {
      const m = gameOpts.baseBet ?? gameOpts.pointValue ?? gameOpts.dollarsPerPoint ?? gameOpts.dotValue ?? gameOpts.snakeAmount ?? gameOpts.frontPot
      if (m !== undefined) gameOpts.betPerHole = Number(m) || 0
    }
    if (noBets) gameOpts.betPerHole = 0
    // Any game with a Bet type (matchplay/four-ball/stableford/chicago/dogfight added
    // here, stroke/scramble/shamble above) carries it so the app reads mods.betType.
    // Values ('Pot'/'Hole-by-hole'/'Match'/'Per-point') are UNCHANGED — the app maps them.
    if (gameOpts.betType !== undefined && mods.betType === undefined) mods.betType = gameOpts.betType
    gameOpts.mods = mods
    // Side-bet carry: the app reads meta.options.scatsAmount / scatsBet / scatsMode /
    // skinsAmount / dotValue. scatsBet = 'pot'|'perhole' and scatsMode = 'team'|'player'
    // are the EXACT lowercase values the app's GameSetupShell writes/reads.
    if (addons.scats) {
      gameOpts.scatsAmount = addons.scats.buyIn
      gameOpts.scatsBet = (opts.__scatsBet === 'PerHole') ? 'perhole' : 'pot'
      gameOpts.scatsMode = (game.teams === 2 && (opts.__scatsScoreMode || 'Team') !== 'Player') ? 'team' : 'player'
    }
    if (addons.skins) {
      gameOpts.skinsEnabled = true
      gameOpts.skinsAmount = addons.skins.amount
      gameOpts.skinsCarry = addons.skins.carry !== false
      gameOpts.skinsPayout = addons.skins.payout
      gameOpts.skinsIn = addons.skins.in
    }
    if (addons.dots) gameOpts.dotValue = addons.dots.dotValue
    const meta = {
      gameId: game.appGameId || gameKey,
      name: name.trim() || `${game.label} — ${named.length} players`,
      course: { id: course.id, name: course.name, par: course.par, pars: course.pars, strokeIndex: course.strokeIndex, teeName: tee?.name, slope: tee?.slope, rating: tee?.rating, address: [course.address, course.city, course.state].filter(Boolean).join(', ') || null, city: course.city || null, state: course.state || null },
      options: gameOpts,
      handicapsOn,
      noBets: !!noBets,
      allowance: 100,
      addons,
      ...(namedTeams ? { teams: teams.map((t, ti) => ({ name: t.name.trim() || `Team ${ti + 1}` })) } : {}),
    }
    return { meta, players }
  }

  // Staff build: create + play the round on the web (unchanged behaviour).
  const start = async () => {
    const payload = buildPayload(); if (!payload) return
    setBusy(true)
    try {
      const id = await createGame(payload.meta, payload.players)
      onStart(id)
    } catch (e) {
      setBusy(false)
      console.error('createGame failed:', e) // surface the true Firebase error
      alert(`Could not create the game:\n${e?.message || e}`)
    }
  }

  // Members (GGC_ONLY): the website never plays — Review → Send and Save writes
  // the shared /ggGames contract so the companion app plays it on the course.
  const openReview = () => { const p = buildPayload(); if (p) { setSent(false); setReview(p) } }
  const sendAndSave = async () => {
    if (!review) return
    setBusy(true)
    try {
      if (editGameId) await updateMemberGame(editGameId, review.meta, review.players)
      else await saveMemberGame(review.meta, review.players)
      setSent(true)
    }
    catch (e) { console.error('save game failed:', e); alert(`Could not save the game:\n${e?.message || e}`) }
    finally { setBusy(false) }
  }

  if (review) return (
    <GGameReview meta={review.meta} players={review.players} busy={busy} sent={sent} edit={!!editGameId}
      onSend={sendAndSave} onBack={() => { setReview(null); setSent(false) }} onDone={onCancel} />
  )

  return (
    <div className={HUSTLER ? '' : 'card'}>
      <div className={HUSTLER ? 'flex items-center gap-3 mb-5' : 'flex items-center gap-2 mb-3'}>
        {HUSTLER && HUSTLER_GAME_ICONS[gameKey]
          ? <img src={HUSTLER_GAME_ICONS[gameKey]} alt="" aria-hidden="true" style={{ width: 48, height: 48, objectFit: 'contain' }} />
          : <span style={{ fontSize: HUSTLER ? 42 : 22 }}>{game.icon}</span>}
        {HUSTLER
          ? <h3 className="font-extrabold" style={{ fontSize: 34, textTransform: 'uppercase', letterSpacing: '0.01em', color: '#fff' }}>Create {game.label} Game</h3>
          : <h3 className="font-bold text-xl">New {game.label} Game</h3>}
      </div>
      <div className="space-y-5">
        <div className={"flex flex-wrap items-center gap-2" + (HUSTLER ? ' rounded-lg border p-4' : '')}>
          <input className="border rounded px-3 py-2 text-base flex-1 min-w-[200px]" placeholder="Game / group name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
          {rosters.length > 0 && (
            <select className="border rounded px-3 py-2 text-base" defaultValue="" onChange={(e) => { if (e.target.value) loadRoster(e.target.value) }}>
              <option value="">{GGC_ONLY ? 'Load Players' : 'Load roster…'}</option>
              {rosters.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.rows?.length || 0})</option>)}
            </select>
          )}
        </div>

        {/* Course — search the shared course lookup + recent/saved list */}
        <div className="rounded-lg border p-3" style={{ borderColor: '#E5E7EB' }}>
          <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
            <label className={`flex flex-col gap-1 ${GGC_ONLY ? 'text-lg' : 'text-base'} font-semibold text-gray-800`}>{GGC_ONLY ? 'Course Selection' : 'Course'}
              <select className="border rounded px-3 py-2 text-base" value={course.id} onChange={(e) => pickSaved(e.target.value)}>
                {pickList.map((c) => <option key={c.id} value={c.id}>{c.name} · par {c.par}</option>)}
              </select>
            </label>
            <label className={`flex flex-col gap-1 ${GGC_ONLY ? 'text-lg' : 'text-base'} font-semibold text-gray-800`}>Default tee
              <select className="border rounded px-3 py-2 text-base" value={teeIdx} onChange={(e) => setTeeIdx(Number(e.target.value))}>
                {(course.tees || []).map((t, i) => <option key={i} value={i}>{t.name} · {t.rating}/{t.slope}</option>)}
                {(!course.tees || course.tees.length === 0) && <option>No tees</option>}
              </select>
            </label>
            <label className={`flex items-center gap-2 ${GGC_ONLY ? 'text-lg' : 'text-base'} font-semibold text-gray-800 pb-2`}><input type="checkbox" className="w-5 h-5" checked={handicapsOn} onChange={(e) => setHandicapsOn(e.target.checked)} /> Handicaps (net)</label>
            <label className={`flex items-center gap-2 ${GGC_ONLY ? 'text-lg' : 'text-base'} font-semibold text-gray-800 pb-2`} title="No money — game is scored/tracked normally but excluded from settle-up"><input type="checkbox" className="w-5 h-5" checked={noBets} onChange={(e) => setNoBets(e.target.checked)} /> No bets</label>
            <span className="text-sm text-gray-400 pb-2">par {course.par} · {course.location || 'saved'}</span>
          </div>
          <div className="mt-2">
            <input className="border rounded px-3 py-2 text-base w-full max-w-md" placeholder="🔎 Search any course (name)…" value={query} onChange={(e) => setQuery(e.target.value)} />
            {searching && <span className="text-sm text-gray-400 ml-2">searching…</span>}
            {searchErr && !searching ? <div className="text-sm mt-1" style={{ color: searchErr.startsWith('No matches') ? '#6B7280' : '#B42318' }}>{searchErr}</div> : null}
            {results.length > 0 && (
              <div className="mt-1 rounded border" style={{ borderColor: '#E5E7EB', maxHeight: 160, overflowY: 'auto' }}>
                {results.map((r) => (
                  <button key={r.id} className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50" onClick={() => pickResult(r)}>
                    <b>{r.name}</b> <span className="text-gray-400">· {r.location || '—'} · par {r.par} · {r.teeBoxes.length} tee{r.teeBoxes.length === 1 ? '' : 's'}{r.pars ? ' · per-hole ✓' : ''}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Options */}
        <div className={"flex flex-wrap gap-x-8 gap-y-4" + (HUSTLER ? ' rounded-lg border p-4' : '')}>
          {game.options.map((o) => (
            <label key={o.key} className={`${GGC_ONLY ? 'text-lg' : 'text-base'} font-semibold text-gray-800 ${o.type === 'bool' ? 'flex items-center gap-2' : 'flex flex-col gap-1'}`}>
              {o.type === 'bool' && <><input type="checkbox" className="w-5 h-5" checked={!!opts[o.key]} onChange={(e) => setOpts({ ...opts, [o.key]: e.target.checked })} /> {o.label}</>}
              {o.type === 'number' && <>{(() => { const bt = (game.options || []).find((x) => x.key === 'betType'); if (bt && String(o.label).includes('$')) { const d = (bt.labels && bt.labels[opts.betType] != null) ? bt.labels[opts.betType] : opts.betType; return d === 'Per Hole' ? '$/hole' : d === 'Per Point' ? '$/point' : 'Pot' } return o.label })()}<input type="number" className={'w-28 border rounded px-3 py-2 text-base' + ((o.label && String(o.label).includes('$')) ? ' gg-money' : '')} disabled={noBets && !!o.label && String(o.label).includes('$')} value={opts[o.key]} onChange={(e) => setOpts({ ...opts, [o.key]: e.target.value })} /></>}
              {o.type === 'select' && <><span className="inline-flex items-center gap-3">{o.label}{GGC_ONLY && o.key === 'variant' ? <VariantInfo /> : null}</span><select className="border rounded px-3 py-2 text-base" style={{ minWidth: GGC_ONLY ? 150 : undefined }} value={opts[o.key]} onChange={(e) => setOpts({ ...opts, [o.key]: e.target.value })}>{o.options.map((v) => { const d = (o.labels && o.labels[v] != null) ? o.labels[v] : v; return <option key={v} value={v}>{GGC_ONLY ? String(d).toUpperCase() : d}</option> })}</select></>}
            </label>
          ))}
        </div>

        {/* Team Cup — Official vs Custom scoring picker with per-option info. */}
        {gameKey === 'team-cup' && (
          <div className="rounded-lg border p-3" style={{ borderColor: '#E5E7EB' }}>
            <div className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-2">Scoring</div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
              <label className={`flex items-center gap-2 ${GGC_ONLY ? 'text-lg' : 'text-base'} font-bold text-gray-800`}>
                <input type="radio" name="tc-scoring" className="w-5 h-5" checked={(opts.scoringSystem || 'official') === 'official'} onChange={() => setOpts({ ...opts, scoringSystem: 'official' })} /> Official
                <InfoDot><span style={{ display: 'block', fontWeight: 800, fontSize: 24, marginBottom: 12 }}>Official scoring</span><span style={{ display: 'block' }}>Point-a-hole match: win a hole = 1 pt, halve = ½ pt each. Best total over 18 wins (max 18).</span></InfoDot>
              </label>
              <label className={`flex items-center gap-2 ${GGC_ONLY ? 'text-lg' : 'text-base'} font-bold text-gray-800`}>
                <input type="radio" name="tc-scoring" className="w-5 h-5" checked={opts.scoringSystem === 'custom'} onChange={() => setOpts({ ...opts, scoringSystem: 'custom' })} /> Custom
                <InfoDot><span style={{ display: 'block', fontWeight: 800, fontSize: 24, marginBottom: 12 }}>Custom scoring</span><span style={{ display: 'block' }}>Same point-a-hole match, plus a bonus: +1 for winning the front nine and +1 for the back nine (max 20).</span></InfoDot>
              </label>
            </div>
          </div>
        )}

        {/* Side games — grouped into one panel for Golf Hustler; separate panels otherwise. */}
        <div className={HUSTLER ? 'rounded-lg border' : 'space-y-5'}>
        {/* Scats add-on — hidden when Scats IS the primary game (redundant). */}
        {gameKey !== 'scats' && (
        <div className={HUSTLER ? 'p-4' : 'rounded-lg border p-3'} style={{ borderColor: opts.__scats ? '#1B7A3E' : '#E5E7EB' }}>
          <label className={`flex items-center gap-2 ${GGC_ONLY ? 'text-lg' : 'text-base'} font-bold text-gray-800`}><input type="checkbox" checked={!!opts.__scats} onChange={(e) => setOpts({ ...opts, __scats: e.target.checked })} /> <SideIcon k="scats" emoji="💰" /> Scats</label>
          {opts.__scats && (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-3 text-sm">
              <label className="font-semibold text-gray-600">Buy-in $<input type="number" className="w-24 border rounded px-2 py-1.5 text-base ml-1 gg-money" disabled={noBets} value={opts.__scatsBuyIn ?? 10} onChange={(e) => setOpts({ ...opts, __scatsBuyIn: e.target.value })} /></label>
              <label className="flex items-center gap-1 font-semibold text-gray-600"><input type="checkbox" checked={!!opts.__scatsHcp} onChange={(e) => setOpts({ ...opts, __scatsHcp: e.target.checked })} /> Net</label>
              {opts.__scatsHcp && <label className="font-semibold text-gray-600">Mode <select className="border rounded px-2 py-1.5 text-base" value={opts.__scatsMode || 'cap1'} onChange={(e) => setOpts({ ...opts, __scatsMode: e.target.value })}><option value="cap1">{GGC_ONLY ? 'CAP1' : 'cap1'}</option><option value="full">{GGC_ONLY ? 'FULL' : 'full'}</option><option value="lowest">{GGC_ONLY ? 'LOWEST' : 'lowest'}</option></select></label>}
              <label className="font-semibold text-gray-600">Payout <select className="border rounded px-2 py-1.5 text-base" value={opts.__scatsBet || 'Pot'} onChange={(e) => setOpts({ ...opts, __scatsBet: e.target.value })}><option value="Pot">{GGC_ONLY ? 'POT' : 'Pot'}</option><option value="PerHole">{GGC_ONLY ? 'PER HOLE' : 'Per Hole'}</option></select></label>
              {game.teams === 2 && <label className="font-semibold text-gray-600">Score by <select className="border rounded px-2 py-1.5 text-base" value={opts.__scatsScoreMode || 'Team'} onChange={(e) => setOpts({ ...opts, __scatsScoreMode: e.target.value })}><option value="Team">{GGC_ONLY ? 'TEAM' : 'Team'}</option><option value="Player">{GGC_ONLY ? 'PLAYER' : 'Player'}</option></select></label>}
              <span className="text-gray-500">Pot splits by birdies-or-better at the END. Toggle who's in below.</span>
            </div>
          )}
        </div>
        )}

        {/* Skins side-game (GGC_ONLY) — net-capable; settles alongside Wolf.
            Hidden when Skins IS the primary game (redundant). */}
        {GGC_ONLY && gameKey !== 'skins' && (
          <div className={HUSTLER ? 'p-4' : 'rounded-lg border p-3'} style={HUSTLER ? { borderTop: '1px solid #2b2b2b' } : { borderColor: opts.__skins ? '#B8860B' : '#E5E7EB' }}>
            <label className={`flex items-center gap-2 ${GGC_ONLY ? 'text-lg' : 'text-base'} font-bold text-gray-800`}><input type="checkbox" checked={!!opts.__skins} onChange={(e) => setOpts({ ...opts, __skins: e.target.checked })} /> <SideIcon k="skins" emoji="💰" /> Skins (side game)</label>
            {opts.__skins && (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-3 text-sm">
                <label className="font-semibold text-gray-600">$ / skin<input type="number" className="w-24 border rounded px-2 py-1.5 text-base ml-1 gg-money" disabled={noBets} value={opts.__skinsAmount ?? ''} onChange={(e) => setOpts({ ...opts, __skinsAmount: e.target.value })} /></label>
                <label className="flex items-center gap-1 font-semibold text-gray-600"><input type="checkbox" checked={opts.__skinsCarry !== false} onChange={(e) => setOpts({ ...opts, __skinsCarry: e.target.checked })} /> Carryover</label>
                {opts.__skinsCarry !== false && <label className="flex items-center gap-1 font-semibold text-gray-600"><input type="checkbox" checked={!!opts.__skinsDouble} onChange={(e) => setOpts({ ...opts, __skinsDouble: e.target.checked })} /> Double on carry</label>}
                <label className="font-semibold text-gray-600">Payout <select className="border rounded px-2 py-1.5 text-base" value={opts.__skinsPayout || 'split-pot'} onChange={(e) => setOpts({ ...opts, __skinsPayout: e.target.value })}><option value="split-pot">{GGC_ONLY ? 'SPLIT-POT' : 'split-pot'}</option><option value="per-player">{GGC_ONLY ? 'PER-PLAYER' : 'per-player'}</option></select></label>
                <span className="text-gray-500">Lowest {handicapsOn ? 'net ' : ''}score wins each hole. Toggle who's in below. Settles on the results screen.</span>
              </div>
            )}
          </div>
        )}

        {/* Dots side-game (GGC_ONLY) — birdie/eagle auto + tapped junk dots.
            Hidden when Dots IS the primary game (redundant). */}
        {GGC_ONLY && gameKey !== 'dots' && (
          <div className={HUSTLER ? 'p-4' : 'rounded-lg border p-3'} style={HUSTLER ? { borderTop: '1px solid #2b2b2b' } : { borderColor: opts.__dots ? '#2563EB' : '#E5E7EB' }}>
            <label className={`flex items-center gap-2 ${GGC_ONLY ? 'text-lg' : 'text-base'} font-bold text-gray-800`}><input type="checkbox" checked={!!opts.__dots} onChange={(e) => setOpts({ ...opts, __dots: e.target.checked })} /> <SideIcon k="dots" emoji="💠" /> Dots (garbage / junk)</label>
            {opts.__dots && (
              <div className="mt-3 text-sm">
                <label className="font-semibold text-gray-600">$ / dot<input type="number" className="w-24 border rounded px-2 py-1.5 text-base ml-1 gg-money" disabled={noBets} value={opts.__dotValue ?? 1} onChange={(e) => setOpts({ ...opts, __dotValue: e.target.value })} /></label>
                <div className="mt-3">
                  <div className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">Which dots count</div>
                  <div className="flex flex-wrap gap-x-5 gap-y-2">
                    {DOT_CATALOG.map((c) => {
                      const k = '__dot_' + c.key
                      const checked = opts[k] !== undefined ? opts[k] : c.default
                      return (
                        <label key={c.key} className="flex items-center gap-1.5 font-semibold text-gray-700" title={c.desc}>
                          <input type="checkbox" checked={checked} onChange={(e) => setOpts({ ...opts, [k]: e.target.checked })} />
                          {c.emoji} {c.label}{c.auto ? ' (auto)' : ''}
                        </label>
                      )
                    })}
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">Birdie/eagle auto-count from scores. The rest you tap on the scorecard each hole. Settles zero-sum on the results screen.</p>
                </div>
              </div>
            )}
          </div>
        )}
        </div>

        {/* Closest to Hole (Team Cup) — check-to-expand side bet, mirrors Scats. */}
        {gameKey === 'team-cup' && (
          <div className="rounded-lg border p-3" style={{ borderColor: opts.__cth ? '#1B7A3E' : '#E5E7EB' }}>
            <label className={`flex items-center gap-2 ${GGC_ONLY ? 'text-lg' : 'text-base'} font-bold text-gray-800`}><input type="checkbox" checked={!!opts.__cth} onChange={(e) => setOpts({ ...opts, __cth: e.target.checked })} /> 🎯 Closest to Hole (par 3)</label>
            {opts.__cth && (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-3 text-sm">
                <label className="font-semibold text-gray-600">Pot $ / par 3<input type="number" className="w-24 border rounded px-2 py-1.5 text-base ml-1 gg-money" disabled={noBets} value={opts.cthAmount ?? ''} onChange={(e) => setOpts({ ...opts, cthAmount: e.target.value })} /></label>
                <label className="flex items-center gap-1 font-semibold text-gray-600"><input type="checkbox" checked={!!opts.__cthHcp} onChange={(e) => setOpts({ ...opts, __cthHcp: e.target.checked })} /> Net</label>
                <span className="text-gray-500">Par-3 pool. Winner takes the pot; carries forward when none. Pick the winner on the scorecard.</span>
              </div>
            )}
          </div>
        )}

        {/* Players */}
        <div className={HUSTLER ? 'rounded-lg border p-4' : undefined}>
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm font-bold uppercase tracking-wide text-gray-500">Players {(game.teams && gameKey !== 'vegas' && !namedTeams) ? `· Team A = top ${Math.ceil(rows.length / game.teams)}, Team B = rest (reorder ↑↓)` : (game.ordered && !namedTeams) ? (gameKey === 'wolf' ? '· order = Wolf rotation' : '· player order set') : ''}</div>
            {!GGC_ONLY && (
              <div className="flex items-center gap-2">
                {game.ordered && !namedTeams && <button className="btn-ghost text-sm" onClick={shuffle}>🔀 Shuffle</button>}
                <button className="btn-ghost text-sm" onClick={doSaveRoster}>💾 Save roster</button>
              </div>
            )}
          </div>
          {GGC_ONLY && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-bold uppercase tracking-wide text-gray-500">Total players</span>
              <button type="button" className="px-3 py-1 border rounded text-lg leading-none" onClick={() => setTotalPlayers(rows.length - 1)}>−</button>
              <input type="number" min={2} max={8} value={rows.length} onChange={(e) => setTotalPlayers(e.target.value)} className="w-16 border rounded px-2 py-1.5 text-base text-center" inputMode="numeric" />
              <button type="button" className="px-3 py-1 border rounded text-lg leading-none" onClick={() => setTotalPlayers(rows.length + 1)}>+</button>
              {game.ordered && !namedTeams && <button className="btn-ghost text-sm ml-3" onClick={shuffle}>🔀 Shuffle</button>}
              <button className="btn-ghost text-sm" onClick={doSaveRoster}>💾 Save Players</button>
            </div>
          )}
          <table className="w-full text-base" style={{ borderCollapse: 'collapse' }}>
            {/* Header columns use null for hidden columns (filtered out) so each
                <th> lines up with a body <td>; the trailing '' is the delete (×)
                column, which has a cell but no label. */}
            <thead><tr style={{ background: '#f6f6f2', color: '#555' }}>{['#', 'Name', GGC_ONLY ? 'HC Index' : 'Index', GGC_ONLY ? 'GH#' : null, 'Allow %', (handicapsOn || GGC_ONLY) ? 'Tee' : null, (game.ordered && !GGC_ONLY) ? 'Order' : null, opts.__scats ? 'Scats' : null, opts.__skins ? 'Skins' : null, ''].filter((h) => h !== null).map((h, i) => <th key={i} style={{ ...cell, textAlign: 'left' }}>{h}</th>)}</tr></thead>
            <tbody>{rows.map((r, i) => (
              <tr key={i}>
                <td style={cell}>{i + 1}{(game.teams && gameKey !== 'vegas' && !namedTeams) ? <b style={{ color: Math.floor(i / Math.ceil(rows.length / game.teams)) ? '#B45309' : '#1B7A3E', marginLeft: 4 }}>{Math.floor(i / Math.ceil(rows.length / game.teams)) ? 'B' : 'A'}</b> : ''}</td>
                <td style={cell}><input className={'border rounded px-2 py-1.5 text-base ' + (GGC_ONLY ? 'w-full min-w-[200px]' : 'w-44')} value={r.name} onChange={(e) => setRow(i, { name: e.target.value })} placeholder={`Player ${i + 1}`} /></td>
                <td style={cell}><input className="border rounded px-2 py-1.5 text-base w-16" value={r.hcp} onChange={(e) => setRow(i, { hcp: e.target.value })} placeholder="—" /></td>
                {GGC_ONLY && (
                  <td style={cell}>
                    <div className="flex items-center gap-1">
                      <input className="border rounded px-2 py-1.5 text-base w-40" value={r.ghin || ''} onChange={(e) => setRow(i, { ghin: e.target.value.replace(/\D/g, ''), ghinStatus: 'idle' })} placeholder="Not Required" inputMode="numeric" />
                      <button type="button" title="Look up handicap index" className="text-base px-2 py-1.5 rounded" style={{ background: '#EDF4EE', color: '#1B4D2E', opacity: r.ghinStatus === 'loading' ? 0.6 : 1 }} disabled={r.ghinStatus === 'loading'} onClick={() => lookupRowGhin(i)}>
                        {r.ghinStatus === 'loading' ? '…' : r.ghinStatus === 'ok' ? '✓' : r.ghinStatus === 'miss' ? '✕' : '🔎'}
                      </button>
                    </div>
                  </td>
                )}
                <td style={cell}><select className="border rounded px-2 py-1.5 text-base" style={{ minWidth: GGC_ONLY ? 96 : undefined }} value={r.allowance} onChange={(e) => setRow(i, { allowance: e.target.value })}>{ALLOWANCE_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}</select></td>
                {(handicapsOn || GGC_ONLY) && (
                  <td style={cell}>
                    <select className="border rounded px-2 py-1.5 text-base" style={{ minWidth: GGC_ONLY ? 180 : undefined, width: GGC_ONLY ? '100%' : undefined }} value={r.teeIdx ?? 0} onChange={(e) => setRow(i, { teeIdx: Number(e.target.value) })}>
                      {(course.tees || []).map((t, ti) => <option key={ti} value={ti}>{t.name} ({t.rating}/{t.slope})</option>)}
                    </select>
                  </td>
                )}
                {game.ordered && !GGC_ONLY && <td style={cell}><button className="text-sm px-2" onClick={() => move(i, -1)} disabled={i === 0}>↑</button><button className="text-sm px-2" onClick={() => move(i, 1)} disabled={i === rows.length - 1}>↓</button></td>}
                {opts.__scats && <td style={cell}><input type="checkbox" checked={!!r.scatsIn} onChange={(e) => setRow(i, { scatsIn: e.target.checked })} /></td>}
                {opts.__skins && <td style={cell}><input type="checkbox" checked={r.skinsIn !== false} onChange={(e) => setRow(i, { skinsIn: e.target.checked })} /></td>}
                <td style={cell}><button className={GGC_ONLY ? '' : 'text-base'} title="Remove player" style={HUSTLER ? { background: 'transparent', border: 'none', color: '#c9c9c9', padding: '5px 7px', display: 'inline-flex', opacity: rows.length > minPlayers ? 1 : 0.4 } : GGC_ONLY ? { background: '#fff', border: '1px solid #9AA0A6', borderRadius: 6, color: '#000', fontWeight: 900, fontSize: 14, lineHeight: 1, padding: '5px 9px' } : { color: rows.length > minPlayers ? '#B42318' : '#ccc' }} disabled={rows.length <= minPlayers} onClick={() => removeRow(i)}>{HUSTLER ? <Trash2 size={16} /> : '✕'}</button></td>
              </tr>
            ))}</tbody>
          </table>
          <button className="btn-ghost text-sm mt-1" disabled={rows.length >= game.maxPlayers} onClick={addRow}>+ Add player</button>
          {isBls && named.length < 6 && <span className="text-xs text-amber-700 ml-2">Biggest Loser Shuck needs 6+ players.</span>}
        </div>

        {/* Team Cup — named teams + click-to-assign (matches the app's model). */}
        {namedTeams && (
          <div>
            <div className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-2">Teams</div>
            {teams.map((t, ti) => (
              <div key={t.id} className="border rounded-lg p-3 mb-2" style={{ borderColor: '#D8DED8' }}>
                <div className="flex items-center gap-2">
                  <input className="border rounded px-2 py-1.5 text-base flex-1" value={t.name} placeholder={`Team ${ti + 1} name`} onChange={(e) => updateTeamName(t.id, e.target.value)} />
                  <button type="button" title="Remove team" disabled={teams.length <= 2} onClick={() => removeTeam(t.id)} style={{ background: '#fff', border: '1px solid #9AA0A6', borderRadius: 6, color: teams.length > 2 ? '#000' : '#ccc', fontWeight: 900, fontSize: 14, lineHeight: 1, padding: '5px 9px' }}>✕</button>
                </div>
                <div className="text-sm font-extrabold uppercase tracking-wide mt-2 mb-1" style={{ color: '#0F2914' }}>CLICK PLAYERS TO ADD</div>
                {/* Compact team-building: chips show P# = the player's ROW NUMBER
                    above (P1 = row 1). The real name is used everywhere in play. */}
                <div className="border rounded p-2 flex flex-col items-start gap-2" style={{ background: '#fff', borderColor: '#D8DED8' }}>
                  {rows.map((r, ri) => {
                    if (!r.name.trim()) return null
                    const on = t.playerIdxs.includes(ri)
                    return (
                      <button key={ri} type="button" onClick={() => togglePlayerOnTeam(t.id, ri)}
                        style={{ padding: '11px 22px', minWidth: 68, borderRadius: 12, border: '2px solid', borderColor: on ? '#1B7A3E' : '#9AA0A6', background: on ? '#1B7A3E' : '#fff', color: on ? '#fff' : '#0F2914', fontWeight: 900, fontSize: 18 }}>
                        {on ? '✓ ' : ''}P{ri + 1}
                      </button>
                    )
                  })}
                  {named.length === 0 && <span className="text-sm text-gray-400 italic">Add players above first.</span>}
                </div>
              </div>
            ))}
            <button type="button" onClick={addTeam} style={{ border: '2px dashed #1B7A3E', borderRadius: 10, color: '#1B7A3E', fontWeight: 900, padding: '9px 14px', width: '100%', background: '#fff' }}>+ Add Team</button>
          </div>
        )}

        <div className="flex items-center gap-2 pt-2">
          <button className="btn-primary" disabled={busy} onClick={GGC_ONLY ? openReview : start} style={{ backgroundColor: GREEN }}>{GGC_ONLY ? (HUSTLER ? 'Review Game →' : 'Review →') : (busy ? 'Starting…' : 'Start Round →')}</button>
          <button className="btn-ghost text-sm" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  )
}
